import { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import type { Span } from "types/spans.types"

import { selectCurrentOrgId } from "features/store/orgSlice"
import { useLazyPowerlineSpansQuery } from "features/api"
import { fetchAllPowerlineSpans } from "helpers/utils/spans"

interface UseTowerSpansProps {
  towerId: number
  powerlineIds: number[]
}

/**
 * The spans that hang from a tower, on every line that lists it. Spans cannot be
 * filtered by tower, so this walks the spans of each of those lines.
 */
export const useTowerSpans = ({ towerId, powerlineIds }: UseTowerSpansProps) => {
  const orgId = useSelector(selectCurrentOrgId)
  const [spans, setSpans] = useState<Span[]>([])
  const [loading, setLoading] = useState(true)
  const [getPowerlineSpans] = useLazyPowerlineSpansQuery()

  // A new array with the same lines should not fetch again.
  const idsKey = powerlineIds.join(",")

  useEffect(() => {
    const ids = idsKey ? idsKey.split(",").map(Number) : []
    if (!orgId || ids.length === 0) {
      setSpans([])
      setLoading(false)
      return
    }
    let isMounted = true
    const fetchAll = async () => {
      setLoading(true)
      try {
        const perLine = await Promise.all(
          ids.map((powerlineId) =>
            fetchAllPowerlineSpans(getPowerlineSpans, powerlineId, Number(orgId)),
          ),
        )
        if (isMounted) {
          setSpans(perLine.flat().filter((span) => span.tower_ids.includes(towerId)))
        }
      } catch {
        if (isMounted) setSpans([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    fetchAll()
    return () => {
      isMounted = false
    }
  }, [orgId, idsKey, towerId, getPowerlineSpans])

  return { spans, isLoading: loading }
}
